import * as Yup from "yup";
import { validationResult } from "./patterns";
import { runFlatFormSchema } from "./yupFormUtils";

const WAF_ACTION_MIN = 0;
const WAF_ACTION_MAX = 2;
const SMART_PARSE_SENSITIVITY_VALUES = new Set([1, 2, 3]);
const SIGNATURE_WAF_LEVEL_MIN = 1;
const SIGNATURE_WAF_LEVEL_MAX = 4;
const CHALLENGE_KIND_VALUES = new Set(["invisible", "managed", "adaptive", "interactive"]);

function validateWafActionValue(value) {
  const n = typeof value === "number" ? value : parseInt(value, 10);
  if (!Number.isFinite(n) || n < WAF_ACTION_MIN || n > WAF_ACTION_MAX) {
    return validationResult(false, "Select a valid WAF action");
  }
  return validationResult(true);
}

function validateSmartParseSensitivityValue(value) {
  const n = typeof value === "number" ? value : parseInt(value, 10);
  if (!Number.isFinite(n) || !SMART_PARSE_SENSITIVITY_VALUES.has(n)) {
    return validationResult(false, "Select a valid sensitivity level");
  }
  return validationResult(true);
}

function validateSignatureWafLevelValue(value) {
  const n = typeof value === "number" ? value : parseInt(value, 10);
  if (!Number.isFinite(n) || n < SIGNATURE_WAF_LEVEL_MIN || n > SIGNATURE_WAF_LEVEL_MAX) {
    return validationResult(false, "Select a valid WAF sensitivity level");
  }
  return validationResult(true);
}

function validateChallengeKindValue(value, { wafAction } = {}) {
  if (wafAction !== 2) return validationResult(true);
  if (value == null || value === "") return validationResult(true);
  if (!CHALLENGE_KIND_VALUES.has(String(value))) {
    return validationResult(false, "Select a valid challenge kind");
  }
  return validationResult(true);
}

function fieldTest(validateFn, options) {
  return function (value) {
    const result = validateFn(value, options);
    return result.valid || this.createError({ message: result.message });
  };
}

function drawerSchema(type) {
  const shape = {
    selectedSiteId: Yup.string().test("selectedSiteId", function (value) {
      const siteId = String(value ?? "").trim();
      if (!siteId || siteId === "overview") {
        return this.createError({ message: "Select a site" });
      }
      return true;
    }),
  };

  if (type === "signature") {
    shape.waf_action_sd_sig = Yup.mixed().test("waf_action_sd_sig", fieldTest(validateWafActionValue));
  } else if (type === "smartparse") {
    shape.smartparse_sensitivity = Yup.mixed().test(
      "smartparse_sensitivity",
      fieldTest(validateSmartParseSensitivityValue),
    );
    shape.waf_action_smartparse = Yup.mixed().test(
      "waf_action_smartparse",
      fieldTest(validateWafActionValue),
    );
  }

  return Yup.object(shape);
}

function signatureSchema() {
  return Yup.object({
    waf_action: Yup.mixed().test("waf_action", fieldTest(validateWafActionValue)),
    signature_waf_level: Yup.mixed().test("signature_waf_level", fieldTest(validateSignatureWafLevelValue)),
    waf_challenge_kind_sd_sig: Yup.mixed().test("waf_challenge_kind_sd_sig", function (value) {
      const action = this.parent.waf_action;
      const result = validateChallengeKindValue(value, { wafAction: action });
      return result.valid || this.createError({ message: result.message });
    }),
  });
}

function smartParseSchema() {
  return Yup.object({
    smart_parse_sensitivity: Yup.mixed().test(
      "smart_parse_sensitivity",
      fieldTest(validateSmartParseSensitivityValue),
    ),
    waf_action_smart_parse: Yup.mixed().test(
      "waf_action_smart_parse",
      fieldTest(validateWafActionValue),
    ),
    waf_challenge_kind_smart_parse: Yup.mixed().test("waf_challenge_kind_smart_parse", function (value) {
      const action = this.parent.waf_action_smart_parse;
      const result = validateChallengeKindValue(value, { wafAction: action });
      return result.valid || this.createError({ message: result.message });
    }),
  });
}

export function validateWafSettingsForm(input = {}) {
  const variant = input.variant || "signature";
  if (variant === "global") {
    return { valid: true, errors: {} };
  }

  let schema;
  let values;

  if (variant === "drawer") {
    schema = drawerSchema(input.type);
    values = {
      selectedSiteId: input.selectedSiteId,
      waf_action_sd_sig: input.waf_action_sd_sig,
      smartparse_sensitivity: input.smartparse_sensitivity,
      waf_action_smartparse: input.waf_action_smartparse ?? input.waf_action_smart_parse,
    };
  } else if (variant === "signature") {
    schema = signatureSchema();
    values = {
      waf_action: input.waf_action ?? input.waf_action_sd_sig,
      signature_waf_level: input.signature_waf_level,
      waf_challenge_kind_sd_sig: input.waf_challenge_kind_sd_sig ?? input.challenge_kind,
    };
  } else if (variant === "smartparse") {
    schema = smartParseSchema();
    values = {
      smart_parse_sensitivity: input.smart_parse_sensitivity ?? input.smartparse_sensitivity,
      waf_action_smart_parse: input.waf_action_smart_parse ?? input.waf_action ?? input.waf_action_smartparse,
      waf_challenge_kind_smart_parse: input.waf_challenge_kind_smart_parse ?? input.challenge_kind,
    };
  } else {
    return { valid: true, errors: {} };
  }

  return runFlatFormSchema(schema, values);
}
